import React from 'react'
import { Link } from 'react-router-dom'
import { ArrowRightIcon } from '@heroicons/react/solid'

import SEOHead from '../utilities/SEOHead'
import { ItemListSchema, BreadcrumbSchema } from '../utilities/StructuredData'
import MarketingNavBar from '../leevo_ui/NavBar/MarketingNavBar/MarketingNavBar'
import MarketingPageFooter from '../leevo_ui/MarketingPageFooter/MarketingPageFooter'
import SimpleJustifiedCTA from '../leevo_ui/SimpleJustifiedCTA/SimpleJustifiedCTA'
import { getAllSportContent } from '../../data/sportContent'

const SPORT_CATEGORIES = {
  'Ice & Winter': ['ice-skating'],
  'Gymnastics & Dance': ['gymnastics', 'tumbling', 'dance', 'cheerleading'],
  'Aquatics': ['swimming', 'rowing'],
  'Racquet Sports': ['tennis', 'pickleball'],
  'Team Sports': ['soccer', 'basketball', 'baseball', 'volleyball', 'football', 'lacrosse'],
  'Combat Sports': ['martial-arts', 'wrestling', 'fencing'],
  'Individual Sports': ['golf', 'track-and-field', 'climbing']
}

export default function SportsIndexPage() {
  const allSports = getAllSportContent()
  const sportsMap = Object.fromEntries(allSports.map((s) => [s.slug, s]))

  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Sports', path: '/sports' }
  ]

  const sportListItems = allSports.map((sport) => ({
    name: `${sport.name} Software`,
    description: sport.metaDescription,
    url: `/sports/${sport.slug}`
  }))

  return (
    <>
      <SEOHead
        title="Sports Program Management Software for Every Sport"
        description="Leevo works for ice skating, gymnastics, swimming, soccer, basketball, martial arts, dance, and 15+ other sports. Find the right solution for your program."
        path="/sports"
        keywords={[
          'sports management software',
          'sports program software',
          'class registration software',
          'skill tracking software',
          'youth sports software',
          'sports facility software'
        ]}
      />
      <ItemListSchema name="Sports Supported by Leevo" description="Complete list of sports programs Leevo supports" items={sportListItems} />
      <BreadcrumbSchema items={breadcrumbs} />

      <div className="bg-white dark:bg-gray-800">
        <MarketingNavBar />

        <main>
          <section className="relative overflow-hidden bg-gradient-to-br from-purple-700 to-purple-900 dark:from-purple-800 dark:to-purple-950">
            <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:60px_60px]" />
            <div className="relative px-4 py-20 mx-auto max-w-7xl sm:px-6 lg:px-8">
              <div className="text-center">
                <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl text-balance">
                  One Platform, Every Sport
                </h1>
                <p className="max-w-3xl mx-auto mt-6 text-xl text-purple-100 text-balance">
                  Leevo is built to manage sports programs of all types. Whether you run an ice rink, gymnastics gym,
                  swim school, or any other sports facility, we have you covered.
                </p>
                <div className="mt-10">
                  <Link
                    to="/onboarding"
                    className="inline-flex items-center px-8 py-4 text-lg font-semibold text-purple-700 bg-white rounded-lg shadow-lg hover:bg-purple-50 transition-colors"
                  >
                    Start Your Free Trial
                    <ArrowRightIcon className="w-5 h-5 ml-2" />
                  </Link>
                </div>
              </div>
            </div>
          </section>

          <section className="py-20 bg-gray-50 dark:bg-gray-900">
            <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl text-balance">
                  Find Your Sport
                </h2>
                <p className="max-w-2xl mx-auto mt-4 text-lg text-gray-600 dark:text-gray-300 text-balance">
                  Click on your sport to see how Leevo can help manage your program
                </p>
              </div>

              {Object.entries(SPORT_CATEGORIES).map(([category, slugs]) => (
                <div key={category} className="mb-12">
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-6">{category}</h3>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {slugs.map((slug) => {
                      const sport = sportsMap[slug]
                      if (!sport) return null
                      return (
                        <Link
                          key={slug}
                          to={`/sports/${slug}`}
                          className="group p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-md transition-all hover:scale-[1.02]"
                        >
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400">
                            {sport.name}
                          </h4>
                          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                            {sport.hero.subheadline}
                          </p>
                          <div className="mt-4 flex items-center text-purple-600 dark:text-purple-400 text-sm font-medium">
                            Learn more
                            <ArrowRightIcon className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </Link>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="py-20 bg-white dark:bg-gray-800">
            <div className="px-4 mx-auto max-w-4xl sm:px-6 lg:px-8 text-center">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl text-balance">
                Don't See Your Sport?
              </h2>
              <p className="mt-4 text-lg text-gray-600 dark:text-gray-300 text-balance">
                Leevo is flexible enough to work with any sports program. Our customizable curriculum and skill tracking
                can adapt to your specific needs.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/onboarding"
                  className="inline-flex items-center justify-center px-6 py-3 text-base font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Start Free Trial
                </Link>
                <a
                  href="mailto:hello@goleevo.com"
                  className="inline-flex items-center justify-center px-6 py-3 text-base font-medium text-purple-600 dark:text-purple-400 border border-purple-600 dark:border-purple-400 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-colors"
                >
                  Contact Us
                </a>
              </div>
            </div>
          </section>

          <SimpleJustifiedCTA showTrialButton />
        </main>

        <MarketingPageFooter />
      </div>
    </>
  )
}
